import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import toast from "react-hot-toast";
import { api } from "../utils/api";
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  ClockIcon,
  DocumentDuplicateIcon,
  XMarkIcon,
  MapPinIcon,
} from "@heroicons/react/24/outline";

const GestionePostazioni = () => {
  const { user, activeCongregazione } = useAuth();
  const [postazioni, setPostazioni] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPostazione, setEditingPostazione] = useState(null);
  const [cloneSourcePostazione, setCloneSourcePostazione] = useState(null);
  const [savingPostazione, setSavingPostazione] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [formData, setFormData] = useState({
    luogo: "",
    indirizzo: "",
    max_proclamatori: 3,
    stato: "attiva",
    temporanea: false,
    valid_from: "",
    valid_to: "",
    slotOrari: [{ orario_inizio: "", orario_fine: "", max_volontari: 3 }],
    giorni_settimana: [],
  });
  const canManagePostazioni =
    user?.ruolo === "admin" || user?.ruolo === "super_admin";

  const requireCongregazioneSelezionata = () => {
    if (user?.ruolo === "super_admin" && !activeCongregazione?.id) {
      toast.error(
        "Seleziona una congregazione attiva dalla pagina Congregazioni per continuare."
      );
      return false;
    }
    return true;
  };

  useEffect(() => {
    const loadPostazioni = async () => {
      if (!requireCongregazioneSelezionata()) {
        setPostazioni([]);
        setLoading(false);
        return;
      }

      try {
        const response = await api.get("/postazioni");
        const data = response.data;
        // Assicuriamoci che ogni postazione abbia un array slot_orari
        const postazioniProcessate = data.map((postazione) => ({
          ...postazione,
          slot_orari: postazione.slot_orari || [],
        }));
        setPostazioni(postazioniProcessate);
      } catch (error) {
        console.error("Errore:", error);
        if (error.response?.status === 401) {
          toast.error("Sessione scaduta. Effettua nuovamente l'accesso.");
        } else {
          toast.error(error.response?.data?.message || "Errore nel caricamento delle postazioni");
        }
      } finally {
        setLoading(false);
      }
    };

    loadPostazioni();
  }, [activeCongregazione]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Popola il form quando si seleziona una postazione per la modifica/duplicazione
  useEffect(() => {
    if (editingPostazione) {
      setFormData({
        luogo: editingPostazione.luogo,
        indirizzo: editingPostazione.indirizzo,
        max_proclamatori: editingPostazione.max_proclamatori || 3,
        stato: editingPostazione.stato || "attiva",
        temporanea: Boolean(editingPostazione.temporanea),
        valid_from: editingPostazione.valid_from || "",
        valid_to: editingPostazione.valid_to || "",
        giorni_settimana: editingPostazione.giorni_settimana || [],
        slotOrari:
          editingPostazione.slot_orari?.map((slot) => ({
            id: slot.id,
            orario_inizio:
              slot.orario_inizio?.split(":").slice(0, 2).join(":") || "",
            orario_fine:
              slot.orario_fine?.split(":").slice(0, 2).join(":") || "",
            max_volontari: slot.max_volontari || 3,
          })) || [{ orario_inizio: "", orario_fine: "", max_volontari: 3 }],
      });
      setCloneSourcePostazione(null);
    } else if (cloneSourcePostazione) {
      setFormData({
        luogo: `${cloneSourcePostazione.luogo} (copia)`,
        indirizzo: cloneSourcePostazione.indirizzo,
        max_proclamatori: cloneSourcePostazione.max_proclamatori || 3,
        stato: cloneSourcePostazione.stato || "attiva",
        temporanea: Boolean(cloneSourcePostazione.temporanea),
        valid_from: cloneSourcePostazione.valid_from || "",
        valid_to: cloneSourcePostazione.valid_to || "",
        giorni_settimana: cloneSourcePostazione.giorni_settimana || [],
        slotOrari:
          cloneSourcePostazione.slot_orari?.map((slot) => ({
            orario_inizio:
              slot.orario_inizio?.split(":").slice(0, 2).join(":") || "",
            orario_fine:
              slot.orario_fine?.split(":").slice(0, 2).join(":") || "",
            max_volontari: slot.max_volontari || 3,
          })) || [{ orario_inizio: "", orario_fine: "", max_volontari: 3 }],
      });
    } else {
      // Reset del form quando non si sta modificando
      resetForm();
    }
  }, [editingPostazione, cloneSourcePostazione]);

  const giorniSettimana = {
    1: "Lunedì",
    2: "Martedì",
    3: "Mercoledì",
    4: "Giovedì",
    5: "Venerdì",
    6: "Sabato",
    7: "Domenica",
  };

  const formatGiorni = (giorni) => {
    return giorni.map((g) => giorniSettimana[g]).join(", ");
  };

  const getStatusColor = (stato) => {
    return stato === "attiva"
      ? "bg-green-100 text-green-800"
      : "bg-red-100 text-red-800";
  };

  const addSlotOrario = () => {
    setFormData((prev) => ({
      ...prev,
      slotOrari: [
        ...prev.slotOrari,
        {
          orario_inizio: "",
          orario_fine: "",
          max_volontari: prev.max_proclamatori || 3,
        },
      ],
    }));
  };

  const removeSlotOrario = (index) => {
    if (formData.slotOrari.length > 1) {
      setFormData((prev) => ({
        ...prev,
        slotOrari: prev.slotOrari.filter((_, i) => i !== index),
      }));
    }
  };

  const updateSlotOrario = (index, field, value) => {
    // Rimuovi i secondi dal valore dell'orario (mantieni solo HH:MM)
    let cleanValue;
    if (field.includes("orario")) {
      cleanValue = value.split(":").slice(0, 2).join(":");
    } else if (field === "max_volontari") {
      const parsed = parseInt(value, 10);
      cleanValue = Math.min(10, Math.max(1, parsed || 1));
    } else {
      cleanValue = value;
    }

    setFormData((prev) => ({
      ...prev,
      slotOrari: prev.slotOrari.map((slot, i) =>
        i === index ? { ...slot, [field]: cleanValue } : slot
      ),
    }));
  };

  const handleGiornoChange = (giorno) => {
    setFormData((prev) => ({
      ...prev,
      giorni_settimana: prev.giorni_settimana.includes(giorno)
        ? prev.giorni_settimana.filter((g) => g !== giorno)
        : [...prev.giorni_settimana, giorno],
    }));
  };

  const resetForm = () => {
    setFormData({
      luogo: "",
      indirizzo: "",
      max_proclamatori: 3,
      stato: "attiva",
      temporanea: false,
      valid_from: "",
      valid_to: "",
      slotOrari: [{ orario_inizio: "", orario_fine: "", max_volontari: 3 }],
      giorni_settimana: [],
    });
  };

  const openCreateModal = () => {
    setEditingPostazione(null);
    setCloneSourcePostazione(null);
    setShowModal(true);
  };

  const openEditModal = (postazione) => {
    setCloneSourcePostazione(null);
    setEditingPostazione(postazione);
    setShowModal(true);
  };

  const openCloneModal = (postazione) => {
    setEditingPostazione(null);
    setCloneSourcePostazione(postazione);
    setShowModal(true);
  };

  const handleDelete = async (postazioneId) => {
    if (
      !window.confirm(
        "Sei sicuro di voler eliminare questa postazione? Questa azione non può essere annullata."
      )
    ) {
      return;
    }

    try {
        await api.delete(`/postazioni/${postazioneId}`);
        setPostazioni((prev) => prev.filter((p) => p.id !== postazioneId));
        toast.success("Postazione eliminata con successo!");
    } catch (error) {
      console.error("Errore:", error);
      if (error.response?.status === 401) {
        toast.error("Sessione scaduta. Effettua nuovamente l'accesso.");
      } else {
        toast.error(error.response?.data?.message || "Errore nell'eliminazione della postazione");
      }
    }
  };

  const handleToggleStato = async (postazioneId, currentStato) => {
    const newStato = currentStato === "attiva" ? "inattiva" : "attiva";
    const action = newStato === "attiva" ? "attivare" : "disattivare";

    if (
      !window.confirm(
        `Sei sicuro di voler ${action} questa postazione? Le postazioni inattive non saranno visibili ai proclamatori.`
      )
    ) {
      return;
    }

    try {
      if (!requireCongregazioneSelezionata()) {
        return;
      }

      const response = await api.patch(
        `/postazioni/${postazioneId}/toggle-stato`,
        { stato: newStato }
      );

      const updatedPostazione = response.data;
      setPostazioni((prev) =>
        prev.map((p) => (p.id === postazioneId ? updatedPostazione : p))
      );
      toast.success(
        `Postazione ${
          newStato === "attiva" ? "attivata" : "disattivata"
        } con successo!`
      );
    } catch (error) {
      console.error("Errore:", error);
      if (error.response?.status === 401) {
        toast.error("Sessione scaduta. Effettua nuovamente l'accesso.");
      } else {
        toast.error(error.response?.data?.message || "Errore nel cambio di stato della postazione");
      }
    }
  };

  const handleSyncDisponibilita = async () => {
    if (
      window.confirm(
        "Vuoi sincronizzare le disponibilità con le configurazioni delle postazioni? Questa operazione eliminerà le disponibilità non coerenti."
      )
    ) {
      try {
        if (!requireCongregazioneSelezionata()) {
          return;
        }

        const response = await api.post("/postazioni/sync-disponibilita", {
          congregazione_id: activeCongregazione?.id,
        });

        const result = response.data;
        toast.success(
          `Sincronizzazione completata! Eliminate ${result.deletedCount} disponibilità non coerenti.`
        );

        // Mostra statistiche dettagliate
        if (result.stats && result.stats.length > 0) {
          const statsMessage = result.stats
            .map(
              (stat) =>
                `${stat.luogo}: ${stat.disponibilita_rimanenti} disponibilità (${stat.data_inizio} - ${stat.data_fine})`
            )
            .join("\n");
          console.log("Statistiche sincronizzazione:", statsMessage);
        }
      } catch (error) {
        console.error("Errore:", error);
        if (error.response?.status === 401) {
          toast.error("Sessione scaduta. Effettua nuovamente l'accesso.");
        } else {
          toast.error(error.response?.data?.message || "Errore nella sincronizzazione");
        }
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (savingPostazione) {
      return;
    }

    // Validazione
    if (!formData.luogo.trim() || !formData.indirizzo.trim()) {
      toast.error("Inserisci luogo e indirizzo");
      return;
    }

    if (formData.giorni_settimana.length === 0) {
      toast.error("Seleziona almeno un giorno della settimana");
      return;
    }

    // Validazione slot orari
    for (let i = 0; i < formData.slotOrari.length; i++) {
      const slot = formData.slotOrari[i];
      if (!slot.orario_inizio || !slot.orario_fine) {
        toast.error(`Completa gli orari per lo slot ${i + 1}`);
        return;
      }
      if (slot.orario_inizio >= slot.orario_fine) {
        toast.error(
          `L'orario di fine deve essere successivo all'inizio per lo slot ${
            i + 1
          }`
        );
        return;
      }
    }

    if (formData.temporanea) {
      if (!formData.valid_from || !formData.valid_to) {
        toast.error("Inserisci data inizio e fine validità per una postazione temporanea");
        return;
      }
      if (formData.valid_from > formData.valid_to) {
        toast.error("La data di inizio validità deve precedere quella di fine");
        return;
      }
    }

    if (cloneSourcePostazione) {
      const originalName = (cloneSourcePostazione.luogo || "").trim().toLowerCase();
      const currentName = formData.luogo.trim().toLowerCase();
      if (!currentName || currentName === originalName) {
        toast.error("Il nome della postazione duplicata deve essere diverso da quello originale.");
        return;
      }
      if (
        cloneSourcePostazione.temporanea &&
        formData.temporanea &&
        formData.valid_from === (cloneSourcePostazione.valid_from || "") &&
        formData.valid_to === (cloneSourcePostazione.valid_to || "")
      ) {
        toast.error("Modifica l'intervallo di validità: non può essere identico a quello originale.");
        return;
      }
    }

    try {
      if (!requireCongregazioneSelezionata()) {
        return;
      }
      setSavingPostazione(true);

      const postazioneData = {
        luogo: formData.luogo.trim(),
        indirizzo: formData.indirizzo.trim(),
        max_proclamatori: formData.max_proclamatori,
        giorni_settimana: formData.giorni_settimana,
        stato: formData.stato,
        temporanea: formData.temporanea,
        valid_from: formData.temporanea ? formData.valid_from : null,
        valid_to: formData.temporanea ? formData.valid_to : null,
        slot_orari: formData.slotOrari.map((slot) => ({
          ...(slot.id ? { id: slot.id } : {}),
          orario_inizio: slot.orario_inizio,
          orario_fine: slot.orario_fine,
          max_volontari:
            slot.max_volontari || formData.max_proclamatori || 3,
        })),
      };

      if (user?.ruolo === "super_admin" && activeCongregazione?.id) {
        postazioneData.congregazione_id = activeCongregazione.id;
      }

      const isEditing = editingPostazione !== null;
      const isCloning = cloneSourcePostazione !== null;
      let response;

      if (isEditing) {
        response = await api.put(`/postazioni/${editingPostazione.id}`, postazioneData);
      } else {
        response = await api.post("/postazioni", postazioneData);
      }

      const updatedPostazione = response.data;

      if (isEditing) {
        // Aggiorna la postazione esistente nella lista
        setPostazioni((prev) =>
          prev.map((p) =>
            p.id === editingPostazione.id ? updatedPostazione : p
          )
        );
        toast.success("Postazione aggiornata con successo!");
      } else {
        // Aggiungi la nuova postazione alla lista
        setPostazioni((prev) => [...prev, updatedPostazione]);
        toast.success(
          isCloning
            ? "Postazione duplicata con successo!"
            : "Postazione creata con successo!"
        );
      }

      setShowModal(false);
      setEditingPostazione(null);
      setCloneSourcePostazione(null);
      resetForm();
    } catch (error) {
      console.error("Errore:", error);
      if (error.response?.status === 401) {
        toast.error("Sessione scaduta. Effettua nuovamente l'accesso.");
      } else {
        const isEditing = editingPostazione !== null;
        const isCloning = cloneSourcePostazione !== null;
        toast.error(
          error.response?.data?.message ||
            `Errore nella ${
              isEditing ? "modifica" : isCloning ? "duplicazione" : "creazione"
            } della postazione`
        );
      }
    } finally {
      setSavingPostazione(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-20 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div
        className={`flex ${
          isMobile ? "flex-col space-y-4" : "justify-between items-center"
        }`}
      >
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Gestione Postazioni
          </h1>
          <p className="text-gray-600 mt-1">
            Crea, modifica e gestisci le postazioni e i loro orari
          </p>
          <div className="accent-line mt-3" />
        </div>
        <div
          className={`flex ${
            isMobile ? "flex-col space-y-3 w-full" : "space-x-3"
          }`}
        >
          <button
            onClick={openCreateModal}
            className={`btn-primary flex items-center ${
              isMobile ? "justify-center w-full" : ""
            }`}
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Nuova Postazione
          </button>
          <button
            onClick={handleSyncDisponibilita}
            className={`btn-secondary flex items-center ${
              isMobile ? "justify-center w-full" : ""
            }`}
            title="Sincronizza disponibilità con configurazioni postazioni"
          >
            <ClockIcon className="h-5 w-5 mr-2" />
            Sincronizza Disponibilità
          </button>
        </div>
      </div>

      {/* Lista Postazioni */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">
            Elenco Postazioni ({postazioni.length})
          </h3>
        </div>
        <div className="accent-line mx-6" />
        {isMobile ? (
          <div className="p-4 space-y-4">
            {postazioni.length > 0 ? (
              <div className="space-y-4">
                {postazioni.map((postazione) => (
                  <div
                    key={postazione.id}
                    className="relative border border-gray-200 rounded-lg p-4 pl-6 space-y-4 shadow-sm"
                  >
                    <span
                      className="accent-bar top-4 bottom-4"
                      aria-hidden="true"
                    />
                    <div>
                      <p className="text-sm uppercase text-gray-500">
                        Postazione
                      </p>
                      <p className="text-xl font-semibold text-gray-900">
                        {postazione.luogo}
                      </p>
                      <p className="text-sm text-gray-500">
                        {postazione.indirizzo}
                      </p>
                      {postazione.temporanea && (
                        <p className="mt-2 inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                          Temporanea: {postazione.valid_from} →{" "}
                          {postazione.valid_to}
                        </p>
                      )}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="p-3 bg-gray-50 rounded-lg flex items-center justify-between">
                        <div>
                          <p className="text-xs text-gray-500">
                            Max Proclamatori
                          </p>
                          <p className="text-base font-semibold text-gray-900">
                            {postazione.max_proclamatori || 3}
                          </p>
                        </div>
                        <PlusIcon className="h-5 w-5 text-gray-400" />
                      </div>
                      <div className="p-3 bg-gray-50 rounded-lg flex items-center justify-between">
                        <div>
                          <p className="text-xs text-gray-500">
                            Turni Assegnati
                          </p>
                          <p className="text-base font-semibold text-gray-900">
                            {postazione.turni_assegnati || 0}
                          </p>
                        </div>
                        <ClockIcon className="h-5 w-5 text-gray-400" />
                      </div>
                      <div className="p-3 bg-gray-50 rounded-lg flex items-center justify-between">
                        <div>
                          <p className="text-xs text-gray-500">Stato</p>
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                              postazione.stato
                            )}`}
                          >
                            {postazione.stato === "attiva"
                              ? "Attiva"
                              : "Inattiva"}
                          </span>
                        </div>
                        <EyeIcon className="h-5 w-5 text-gray-400" />
                      </div>
                    </div>

                    <div>
                      <p className="text-sm font-medium text-gray-700 mb-2">
                        Giorni attivi
                      </p>
                      {postazione.giorni_settimana?.length ? (
                        <div className="flex flex-wrap gap-2">
                          {postazione.giorni_settimana.map((giorno) => (
                            <span
                              key={`${postazione.id}-day-${giorno}`}
                              className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary-100 text-primary-800"
                            >
                              {giorniSettimana[giorno]}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500">
                          Nessun giorno configurato.
                        </p>
                      )}
                    </div>

                    <div>
                      <p className="text-sm font-medium text-gray-700 mb-2">
                        Orari disponibili
                      </p>
                      <div className="space-y-2">
                        {postazione.slot_orari?.length ? (
                          postazione.slot_orari.map((slot, index) => (
                            <div
                              key={`${postazione.id}-slot-${index}`}
                              className="flex items-center justify-between p-3 border rounded-lg"
                            >
                              <div className="flex items-center text-sm text-gray-900">
                                <ClockIcon className="h-4 w-4 mr-2 text-gray-400" />
                                {slot.orario_inizio} - {slot.orario_fine}
                              </div>
                              <span className="text-xs text-gray-500">
                                max {slot.max_volontari}
                              </span>
                            </div>
                          ))
                        ) : (
                          <p className="text-sm text-gray-500">
                            Nessuno slot configurato.
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-2">
                      <button
                        onClick={() =>
                          handleToggleStato(postazione.id, postazione.stato)
                        }
                        className={`flex-1 px-4 py-2 text-sm font-medium rounded-md text-white ${
                          postazione.stato === "attiva"
                            ? "bg-red-600 hover:bg-red-700"
                            : "bg-green-600 hover:bg-green-700"
                        }`}
                      >
                        {postazione.stato === "attiva"
                          ? "Disattiva Postazione"
                          : "Attiva Postazione"}
                      </button>
                      <button
                        onClick={() => openEditModal(postazione)}
                        className="flex-1 px-4 py-2 text-sm font-medium rounded-md border border-primary-200 text-primary-700 hover:bg-primary-50"
                      >
                        Modifica
                      </button>
                      {canManagePostazioni && (
                        <button
                          onClick={() => openCloneModal(postazione)}
                          className="flex-1 px-4 py-2 text-sm font-medium rounded-md border border-gray-200 text-gray-700 hover:bg-gray-50"
                        >
                          Duplica
                        </button>
                      )}
                      <button
                        onClick={() => handleDelete(postazione.id)}
                        className="flex-1 px-4 py-2 text-sm font-medium rounded-md border border-red-200 text-red-700 hover:bg-red-50"
                      >
                        Elimina
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <MapPinIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">
                  Nessuna postazione trovata
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  Crea subito una nuova postazione per iniziare.
                </p>
              </div>
            )}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="table-header">Postazione</th>
                    <th className="table-header">Orari</th>
                    <th className="table-header">Giorni</th>
                    <th className="table-header">Max Proclamatori</th>
                    <th className="table-header">Stato</th>
                    <th className="table-header">Turni Assegnati</th>
                    <th className="table-header">Azioni</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {postazioni.map((postazione) => (
                    <tr key={postazione.id} className="hover:bg-gray-50">
                      <td className="table-cell relative pl-6">
                        <span
                          className="accent-bar top-3 bottom-3"
                          aria-hidden="true"
                        />
                        <div>
                          <div className="font-medium text-gray-900">
                            {postazione.luogo}
                          </div>
                          <div className="text-sm text-gray-500">
                            {postazione.indirizzo}
                          </div>
                          {postazione.temporanea && (
                            <div className="mt-1 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                              Temporanea: {postazione.valid_from} → {postazione.valid_to}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="table-cell">
                        <div className="space-y-1">
                          {postazione.slot_orari?.map((slot, index) => (
                            <div
                              key={index}
                              className="flex items-center text-sm text-gray-900"
                            >
                              <ClockIcon className="h-4 w-4 mr-1 text-gray-400" />
                              {slot.orario_inizio} - {slot.orario_fine}
                              <span className="ml-2 text-xs text-gray-500">
                                (max {slot.max_volontari})
                              </span>
                            </div>
                          ))}
                        </div>
                      </td>
                      <td className="table-cell">
                        <div className="text-sm text-gray-900">
                          {formatGiorni(postazione.giorni_settimana)}
                        </div>
                      </td>
                      <td className="table-cell">
                        <div className="text-sm text-gray-900">
                          <span className="font-medium">
                            {postazione.max_proclamatori || 3}
                          </span>{" "}
                          proclamatori
                        </div>
                      </td>
                      <td className="table-cell">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                            postazione.stato
                          )}`}
                        >
                          {postazione.stato === "attiva" ? "Attiva" : "Inattiva"}
                        </span>
                      </td>
                      <td className="table-cell">
                        <div className="text-sm text-gray-900">
                          {postazione.turni_assegnati || 0} turni
                        </div>
                      </td>
                      <td className="table-cell">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() =>
                              handleToggleStato(postazione.id, postazione.stato)
                            }
                            className={`${
                              postazione.stato === "attiva"
                                ? "text-green-600 hover:text-green-900"
                                : "text-red-600 hover:text-red-900"
                            }`}
                            title={
                              postazione.stato === "attiva"
                                ? "Disattiva postazione"
                                : "Attiva postazione"
                            }
                          >
                            <EyeIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => openEditModal(postazione)}
                            className="text-gray-600 hover:text-gray-900"
                            title="Modifica"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </button>
                          {canManagePostazioni && (
                            <button
                              onClick={() => openCloneModal(postazione)}
                              className="text-gray-600 hover:text-gray-900"
                              title="Duplica"
                            >
                              <DocumentDuplicateIcon className="h-4 w-4" />
                            </button>
                          )}
                          <button
                            onClick={() => handleDelete(postazione.id)}
                            className="text-red-600 hover:text-red-900"
                            title="Elimina"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {postazioni.length === 0 && (
              <div className="text-center py-12">
                <MapPinIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">
                  Nessuna postazione trovata
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  Crea subito una nuova postazione per iniziare.
                </p>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal per aggiunta/modifica postazione */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center bg-gray-600/50 backdrop-blur-sm px-4 py-6 sm:px-6 sm:py-10">
          <div className="w-full max-w-3xl bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden max-h-[90vh] sm:max-h-[80vh] flex flex-col">
              <div className="flex items-start justify-between px-5 py-4 border-b">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">
                  {editingPostazione
                    ? "Modifica Postazione"
                    : cloneSourcePostazione
                    ? "Duplica Postazione"
                    : "Nuova Postazione"}
                  </h3>
                  <p className="text-sm text-gray-500">
                    Compila i dati e scorri per vedere tutti i campi.
                  </p>
                  {cloneSourcePostazione && (
                    <p className="text-xs text-amber-700 mt-1">
                      Modifica almeno il nome e, se temporanea, l'intervallo di validità.
                    </p>
                  )}
                </div>
                <button
                  onClick={() => {
                    setShowModal(false);
                    setEditingPostazione(null);
                    setCloneSourcePostazione(null);
                    resetForm();
                  }}
                  className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            <form
              className="flex flex-col flex-1 overflow-hidden"
              onSubmit={handleSubmit}
            >
              <div className="flex-1 overflow-y-auto px-5 pb-5 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Luogo
                  </label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    value={formData.luogo}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        luogo: e.target.value,
                      }))
                    }
                    placeholder="Es. Piazza del Duomo"
                  />
                  {cloneSourcePostazione && (
                    <p className="text-xs text-amber-700 mt-1">
                      Il nome deve essere diverso dalla postazione originale.
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Indirizzo
                  </label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    value={formData.indirizzo}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        indirizzo: e.target.value,
                      }))
                    }
                    placeholder="Indirizzo completo"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Max Proclamatori per Postazione
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="10"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    value={formData.max_proclamatori}
                    onChange={(e) => {
                      const parsed = Math.min(
                        10,
                        Math.max(1, parseInt(e.target.value, 10) || 1)
                      );
                      setFormData((prev) => ({
                        ...prev,
                        max_proclamatori: parsed,
                        slotOrari: prev.slotOrari.map((slot) => ({
                          ...slot,
                          max_volontari: parsed,
                        })),
                      }));
                    }}
                    placeholder="Numero massimo di proclamatori"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Numero massimo di proclamatori che possono essere assegnati
                    a questa postazione
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Stato Postazione
                  </label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    value={formData.stato}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        stato: e.target.value,
                      }))
                    }
                  >
                    <option value="attiva">Attiva</option>
                    <option value="inattiva">Inattiva</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Le postazioni inattive non saranno visibili ai proclamatori
                    per le disponibilità
                  </p>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <input
                      id="temporanea"
                      type="checkbox"
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      checked={formData.temporanea}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          temporanea: e.target.checked,
                          valid_from: e.target.checked ? prev.valid_from : "",
                          valid_to: e.target.checked ? prev.valid_to : "",
                        }))
                      }
                    />
                    <label
                      htmlFor="temporanea"
                      className="text-sm font-medium text-gray-700"
                    >
                      Postazione temporanea (valida solo in un intervallo di date)
                    </label>
                  </div>
                  {formData.temporanea && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Valida dal
                        </label>
                        <input
                          type="date"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          value={formData.valid_from}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              valid_from: e.target.value,
                            }))
                          }
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Valida fino al
                        </label>
                        <input
                          type="date"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          value={formData.valid_to}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              valid_to: e.target.value,
                            }))
                          }
                        />
                      </div>
                    </div>
                  )}
                  {cloneSourcePostazione &&
                    formData.temporanea &&
                    cloneSourcePostazione.temporanea && (
                      <p className="text-xs text-amber-700">
                        Aggiorna il range: non può essere identico a quello originale.
                      </p>
                    )}
                  <p className="text-xs text-gray-500">
                    Se temporanea è attiva, la postazione è utilizzabile solo per le date comprese nel range indicato.
                  </p>
                </div>

                {/* Slot Orari */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label className="block text-sm font-medium text-gray-700">
                      Slot Orari
                    </label>
                    <button
                      type="button"
                      onClick={addSlotOrario}
                      className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-primary-700 bg-primary-100 hover:bg-primary-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    >
                      <PlusIcon className="h-4 w-4 mr-1" />
                      Aggiungi Slot
                    </button>
                  </div>
                  <div className="space-y-3">
                    {formData.slotOrari.map((slot, index) => (
                      <div
                        key={index}
                        className="flex items-center space-x-3 p-3 border border-gray-200 rounded-md"
                      >
                        <div className="flex-1 grid grid-cols-3 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              Inizio
                            </label>
                            <input
                              type="time"
                              className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                              value={slot.orario_inizio}
                              onChange={(e) =>
                                updateSlotOrario(
                                  index,
                                  "orario_inizio",
                                  e.target.value
                                )
                              }
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              Fine
                            </label>
                            <input
                              type="time"
                              className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                              value={slot.orario_fine}
                              onChange={(e) =>
                                updateSlotOrario(
                                  index,
                                  "orario_fine",
                                  e.target.value
                                )
                              }
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              Capienza
                            </label>
                            <input
                              type="number"
                              min="1"
                              max="10"
                              className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                              value={slot.max_volontari ?? formData.max_proclamatori}
                              onChange={(e) =>
                                updateSlotOrario(
                                  index,
                                  "max_volontari",
                                  e.target.value
                                )
                              }
                            />
                          </div>
                        </div>
                        {formData.slotOrari.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeSlotOrario(index)}
                            className="p-1 text-red-600 hover:text-red-800 focus:outline-none"
                          >
                            <XMarkIcon className="h-5 w-5" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Giorni della settimana
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {Object.entries(giorniSettimana).map(([key, value]) => (
                      <label key={key} className="flex items-center">
                        <input
                          type="checkbox"
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                          checked={formData.giorni_settimana.includes(
                            parseInt(key)
                          )}
                          onChange={() => handleGiornoChange(parseInt(key))}
                        />
                        <span className="ml-2 text-sm text-gray-700">
                          {value}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
              <div className="flex justify-end space-x-3 border-t px-5 py-4 bg-white">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingPostazione(null);
                    resetForm();
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  Annulla
                </button>
                <button
                  type="submit"
                  disabled={savingPostazione}
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {savingPostazione ? (
                    <span className="inline-flex items-center">
                      <span className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></span>
                      Salvataggio...
                    </span>
                  ) : editingPostazione ? (
                    "Aggiorna"
                  ) : (
                    "Crea"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default GestionePostazioni;
